/**
 * Users API Service
 *
 * This service provides methods for user management operations.
 * It handles fetching users and updating user records for the admin
 * user management page.
 *
 * All operations require authentication and admin privileges for mutations.
 *
 * @module services/users
 */

import { User } from '../types/user';
import { getIdToken } from './auth';

/**
 * API configuration from environment variables.
 * Falls back to empty string when not configured.
 */
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Error codes for users-related errors.
 */
export type UsersErrorCode =
  | 'FETCH_FAILED'
  | 'UPDATE_FAILED'
  | 'UNAUTHORIZED'
  | 'NOT_FOUND'
  | 'FORBIDDEN'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

/**
 * Custom error class for users-related errors.
 * Provides structured error information for better error handling.
 */
export class UsersError extends Error {
  /** HTTP status code if applicable */
  statusCode: number;
  /** Error code for programmatic error handling */
  code: UsersErrorCode;
  /** Original error if this error wraps another */
  originalError?: Error;

  constructor(
    message: string,
    statusCode: number,
    code: UsersErrorCode = 'UNKNOWN',
    originalError?: Error
  ) {
    super(message);
    this.name = 'UsersError';
    this.statusCode = statusCode;
    this.code = code;
    this.originalError = originalError;
  }
}

/**
 * Helper function to make authenticated API requests.
 *
 * @param endpoint - The API endpoint (relative to API_BASE_URL)
 * @param options - Optional fetch options
 * @returns Promise resolving to the parsed JSON response
 * @throws UsersError if the request fails
 */
async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    const token = await getIdToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorMessage = await response.text().catch(() => 'Unknown error');
      const code = getErrorCode(response.status);
      throw new UsersError(
        errorMessage || `Request failed with status ${response.status}`,
        response.status,
        code
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof UsersError) {
      throw error;
    }
    // Handle network errors
    throw new UsersError(
      'Network error occurred while fetching user data',
      0,
      'NETWORK_ERROR',
      error instanceof Error ? error : undefined
    );
  }
}

/**
 * Map HTTP status codes to error codes.
 *
 * @param statusCode - HTTP status code
 * @returns Corresponding UsersErrorCode
 */
function getErrorCode(statusCode: number): UsersErrorCode {
  switch (statusCode) {
    case 401:
      return 'UNAUTHORIZED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    default:
      return 'FETCH_FAILED';
  }
}

/**
 * Fetch all users for admin management.
 *
 * Returns a list of all users in the system. Uses GET /users
 * matching the backend route contract.
 *
 * @returns Promise resolving to an array of User objects
 * @throws UsersError if the request fails or user lacks privileges
 *
 * @example
 * ```typescript
 * try {
 *   const users = await getUsers();
 *   console.log('Total users:', users.length);
 * } catch (error) {
 *   if (error instanceof UsersError) {
 *     console.error('Failed to fetch users:', error.message);
 *   }
 * }
 * ```
 */
export async function getUsers(): Promise<User[]> {
  const response = await fetchWithAuth<{ users: User[] }>('/users');
  return response.users;
}

/**
 * Update a user's status or role via PUT /users/{id} with full user object.
 *
 * Sends a PUT request with the complete (or partial) user data to update.
 * This replaces the previous separate PATCH endpoints for status and role.
 *
 * @param userId - The ID of the user to update
 * @param userData - Partial user object containing fields to update
 * @returns Promise resolving to the updated User object
 * @throws UsersError if the request fails, user not found, or lacks admin privileges
 *
 * @example
 * ```typescript
 * try {
 *   // Disable a user
 *   const updated = await updateUser('user-123', { enabled: false });
 *
 *   // Change a user's role
 *   const promoted = await updateUser('user-123', { role: 'admin' });
 * } catch (error) {
 *   if (error instanceof UsersError) {
 *     console.error('Failed to update user:', error.message);
 *   }
 * }
 * ```
 */
export async function updateUser(userId: string, userData: Partial<User>): Promise<User> {
  return fetchWithAuth<User>(`/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
  });
}

/**
 * Users service object for convenient access to all user management functions.
 * Provides a unified interface for user administration operations.
 */
export const usersService = {
  getUsers,
  updateUser,
};

export default usersService;
